export type AllocatedVote = Record<string, string>;
export type BracketType = 'BY_ROUND' | 'BY_MATCHUP';

export interface BracketGridState {
  /**
   * This acts as a cache to keep track of allocated vote options. A user can only vote for one option
   * per match, so a previous vote for a match should be replaced with the new option
   */
  allocatedVoteOptions: AllocatedVote;
  /**
   * Returns true if the voting state is "dirty" - that is, if the user has submitted a bracket and has not cleared their bracket to vote again
   */
  isDirty: boolean;
  /**
   * The type of voting workflow to use. This is used to determine how the bracket grid should be displayed, and how votes should be submitted.
   */
  bracketType: BracketType;
}

type RemoveVoteAction = {
  type: 'REMOVE_VOTE';
  payload: string;
};

type SetVotesAction = {
  type: 'SET_VOTES';
  payload: AllocatedVote;
};

type ResetAction = {
  type: 'RESET';
};

type AddVoteAction = {
  type: 'ADD_VOTE';
  payload: AllocatedVote;
};

type SubmitVotesAction = {
  type: 'SUBMIT';
};

type BracketGridActionTypes = RemoveVoteAction | SetVotesAction | ResetAction | AddVoteAction | SubmitVotesAction;

export function bracketGridReducer(state: BracketGridState, action: BracketGridActionTypes): BracketGridState {
  switch (action.type) {
    case 'REMOVE_VOTE': {
      const { [action.payload]: removed, ...remaining } = state.allocatedVoteOptions;
      const allocatedVoteOptions = { ...remaining };

      return {
        ...state,
        allocatedVoteOptions,
      };
    }
    case 'ADD_VOTE': {
      return {
        ...state,
        allocatedVoteOptions: { ...state.allocatedVoteOptions, ...action.payload },
      };
    }
    case 'SET_VOTES': {
      return {
        ...state,
        allocatedVoteOptions: { ...action.payload },
        isDirty: state.bracketType === 'BY_ROUND' ? Object.entries(action.payload).length > 0 : false,
      };
    }
    case 'RESET': {
      return { ...state, allocatedVoteOptions: {}, isDirty: false };
    }
    case 'SUBMIT': {
      return { ...state, allocatedVoteOptions: {}, isDirty: false };
    }
    default: {
      return state;
    }
  }
}
